import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FiImage, FiSave, FiTrash2 } from 'react-icons/fi';
import { useCompanyData } from '../hooks/useCompanyData';
import { LOGO_CONFIG } from '../config/logoConfig';
import {
  LOGO_TONE_OPTIONS,
  logoPreviewSurfaceClass,
  normalizeLogoTone,
} from '../utils/logoContrast';
import { isFromOnboarding } from '../utils/onboarding';
import OnboardingContinueAction from './OnboardingContinueAction';
import { FormAlert, FormField, formInputClass } from './form';
import CountrySubdivisionSelect from './form/CountrySubdivisionSelect';
import PhoneCountryInput from './form/PhoneCountryInput';

/**
 * Logo y datos de emisor para PDF de comprobantes (recordatorios y envío por email).
 */
export default function ComprobanteBrandingPanel({ modoOscuro, user, textPri, textSec, textMuted, box, inputClass: inputClassProp }) {
  const location = useLocation();
  const onboardingFlow = isFromOnboarding(location.search);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const {
    companyData,
    loading,
    saving,
    logoProcessing,
    saveCompanyData,
    handleCompanyDataChange,
    handleLogoChange,
    loadCompanyData,
  } = useCompanyData();

  const labelClass = textSec || textMuted || textPri;

  const fieldTheme = useMemo(
    () => inputClassProp || (modoOscuro
      ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-gray-500 focus:border-gray-500'
      : 'border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:ring-[#1F80FF]/30 focus:border-[#1F80FF]'),
    [inputClassProp, modoOscuro],
  );

  const tcInput = useMemo(() => formInputClass(fieldTheme), [fieldTheme]);

  const tc = useMemo(
    () => ({
      labelClass,
      inputClass: fieldTheme,
    }),
    [labelClass, fieldTheme],
  );

  const onLogoPick = (event) => {
    const file = event.target.files?.[0];
    if (file) handleLogoChange(file);
    event.target.value = '';
  };

  const clearLogo = () => {
    handleCompanyDataChange('logo', '');
  };

  const handleSave = async () => {
    setSaveSuccess(false);
    const ok = await saveCompanyData();
    if (ok) {
      setSaveSuccess(true);
      loadCompanyData();
    }
  };

  useEffect(() => {
    if (!onboardingFlow) return;
    if (location.hash !== '#comprobante-pdf') return;
    const timer = window.setTimeout(() => {
      document.getElementById('comprobante-pdf')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);
    return () => window.clearTimeout(timer);
  }, [onboardingFlow, location.hash]);

  const logoTone = normalizeLogoTone(companyData.logoTone);
  const previewSurface = logoPreviewSurfaceClass(logoTone, modoOscuro);

  return (
    <div id="comprobante-pdf" className={`${box} rounded-2xl border overflow-hidden scroll-mt-24`}>
      <div className={`px-4 py-3 border-b ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex items-center gap-2.5">
          <div className={`w-9 h-9 rounded-xl border flex items-center justify-center ${modoOscuro ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-white'}`}>
            <FiImage className={`text-base ${modoOscuro ? 'text-gray-300' : 'text-gray-900'}`} />
          </div>
          <div>
            <h2 className={`text-sm font-semibold ${textPri}`}>Comprobantes PDF</h2>
            <p className={`text-xs ${textMuted}`}>
              Logo y datos del emisor en PDFs adjuntos (recordatorios de cobro y envío por email).
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {loading && (
          <p className={`text-xs ${textMuted}`}>Cargando datos…</p>
        )}

        <div className={`rounded-xl border p-4 ${modoOscuro ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50/80'}`}>
          <p className={`text-xs font-medium mb-2 ${textPri}`}>Logotipo</p>
          <p className={`text-[11px] mb-3 ${textMuted}`}>
            Recomendado: {LOGO_CONFIG.PDF_RECOMMENDED_LABEL} (PNG o JPG). Indicá qué color predomina en tu logo para que el PDF lo muestre bien.
          </p>
          <div className="flex flex-wrap items-start gap-4">
            <div
              className={`flex h-16 w-40 shrink-0 items-center justify-center rounded-lg border ${previewSurface}`}
              title="Vista previa como en el PDF"
            >
              {companyData.logo ? (
                <img src={companyData.logo} alt="Logo empresa" className="max-h-14 max-w-[9rem] object-contain" />
              ) : (
                <span className={`text-[11px] ${textMuted}`}>Sin logo</span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-[#1F80FF] px-3 py-2 text-xs font-semibold text-white hover:bg-[#004AAD]">
                Subir imagen
                <input type="file" accept="image/*" className="sr-only" onChange={onLogoPick} />
              </label>
              {companyData.logo && (
                <button
                  type="button"
                  onClick={clearLogo}
                  className={`inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-xs font-semibold ${modoOscuro ? 'border-gray-600 text-gray-200' : 'border-gray-300 text-gray-700'}`}
                >
                  <FiTrash2 className="w-3.5 h-3.5" />
                  Quitar
                </button>
              )}
            </div>
          </div>

          <fieldset className="mt-4 space-y-2 border-0 p-0">
            <legend className={`text-xs font-medium ${textPri}`}>
              ¿Qué color predomina en tu logo?
            </legend>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {LOGO_TONE_OPTIONS.map((opt) => {
                const selected = logoTone === opt.value;
                return (
                  <label
                    key={opt.value}
                    className={`cursor-pointer rounded-lg border p-3 text-left transition-colors ${
                      selected
                        ? modoOscuro
                          ? 'border-[#1F80FF] bg-[#1F80FF]/10'
                          : 'border-[#1F80FF] bg-[#1F80FF]/5'
                        : modoOscuro
                          ? 'border-gray-600 hover:border-gray-500'
                          : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="flex items-start gap-2">
                      <input
                        type="radio"
                        name="logoTone"
                        value={opt.value}
                        checked={selected}
                        onChange={() => handleCompanyDataChange('logoTone', opt.value)}
                        className="mt-0.5 border-gray-400 text-[#1F80FF] focus:ring-[#1F80FF]"
                      />
                      <span>
                        <span className={`block text-xs font-semibold ${textPri}`}>{opt.label}</span>
                        <span className={`mt-0.5 block text-[11px] leading-snug ${textMuted}`}>{opt.hint}</span>
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField label="Nombre en el comprobante" labelClass={labelClass}>
            <input
              type="text"
              value={companyData.name || ''}
              onChange={(e) => handleCompanyDataChange('name', e.target.value)}
              className={tcInput}
              placeholder="Razón social o marca"
              autoComplete="organization"
            />
          </FormField>
          <FormField label="Email de contacto" labelClass={labelClass}>
            <input
              type="email"
              value={companyData.email || ''}
              onChange={(e) => handleCompanyDataChange('email', e.target.value)}
              className={tcInput}
              placeholder="contacto@tuempresa.com"
              autoComplete="email"
            />
          </FormField>
          <FormField label="Dirección" labelClass={labelClass} className="sm:col-span-2">
            <input
              type="text"
              value={companyData.address || ''}
              onChange={(e) => handleCompanyDataChange('address', e.target.value)}
              className={tcInput}
              placeholder="Calle y número"
            />
          </FormField>
          <div className="sm:col-span-2">
            <CountrySubdivisionSelect
              countryName={companyData.country || ''}
              provinceName={companyData.province || ''}
              cityValue={companyData.city || ''}
              showCity
              cityLabel="Ciudad / localidad"
              onCountryChange={(name) => {
                handleCompanyDataChange('country', name);
                handleCompanyDataChange('province', '');
              }}
              onProvinceChange={(name) => handleCompanyDataChange('province', name)}
              onCityChange={(value) => handleCompanyDataChange('city', value)}
              defaultCountryCode="AR"
              countryRequired={false}
              tc={tc}
            />
          </div>
          <PhoneCountryInput
            value={companyData.phone || ''}
            onChange={(phone) => handleCompanyDataChange('phone', phone)}
            countryName={companyData.country || 'Argentina'}
            defaultCountryCode="AR"
            label="Teléfono"
            labelClass={labelClass}
            tc={tc}
            className="sm:col-span-2"
            placeholder="11 2345-6789"
          />
        </div>

        <FormAlert type="info" className="text-xs">
          Si no cargás logo, el PDF usa el logo de Contsuite. El documento sigue siendo interno (no reemplaza factura fiscal ARCA).
        </FormAlert>

        {saveSuccess && (
          <FormAlert type="success" className="text-xs">
            Datos guardados. Ya podés seguir con el siguiente paso del onboarding.
          </FormAlert>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2">
          <OnboardingContinueAction
            stepId="branding"
            visible={onboardingFlow && saveSuccess}
            modoOscuro={modoOscuro}
          />
          <button
            type="button"
            disabled={saving || logoProcessing}
            onClick={handleSave}
            className="inline-flex items-center gap-2 rounded-lg bg-[#1F80FF] px-4 py-2 text-sm font-semibold text-white hover:bg-[#004AAD] disabled:opacity-50"
          >
            <FiSave className="w-4 h-4" />
            {logoProcessing ? 'Procesando imagen…' : saving ? 'Guardando…' : 'Guardar comprobante PDF'}
          </button>
        </div>
      </div>
    </div>
  );
}
